import { motion, type Transition, type Variants } from "motion/react";
import type React from "react";

/**
 * The site's single motion vocabulary. Every animation picks its ease,
 * duration and spring from here so the whole site moves the same way.
 */
export const EASE = [0.22, 1, 0.36, 1] as const;

export const DURATION = {
	/** State change inside a scene (a check, a pill). */
	state: 0.18,
	/** Scroll reveal of a block. */
	reveal: 0.8,
	/** A line or route drawing itself. */
	draw: 1.8,
} as const;

export const SPRING: Transition = {
	type: "spring",
	stiffness: 220,
	damping: 26,
};

/** Softer spring for whole cards flying into place. */
export const SPRING_SOFT: Transition = {
	type: "spring",
	stiffness: 120,
	damping: 20,
	mass: 0.9,
};

const VIEWPORT = { once: true, margin: "0px 0px -12% 0px" } as const;

/** Fades and lifts a block once when it enters the viewport. */
export function Reveal({
	children,
	className,
	delay = 0,
	as = "div",
}: {
	children: React.ReactNode;
	className?: string;
	delay?: number;
	as?: "div" | "li" | "section" | "figure";
}) {
	const Component = motion[as];
	return (
		<Component
			className={className}
			initial={{ opacity: 0, y: 18 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={VIEWPORT}
			transition={{ duration: DURATION.reveal, ease: EASE, delay }}
		>
			{children}
		</Component>
	);
}

const groupVariants: Variants = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.08 } },
};

const itemVariants: Variants = {
	hidden: { opacity: 0, y: 18 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { duration: DURATION.reveal, ease: EASE },
	},
};

/** Staggers its `RevealItem` children as the group enters the viewport. */
export function RevealGroup({
	children,
	className,
	as = "div",
}: {
	children: React.ReactNode;
	className?: string;
	as?: "div" | "ul" | "ol" | "dl";
}) {
	const Component = motion[as];
	return (
		<Component
			className={className}
			variants={groupVariants}
			initial="hidden"
			whileInView="visible"
			viewport={VIEWPORT}
		>
			{children}
		</Component>
	);
}

export function RevealItem({
	children,
	className,
	as = "div",
}: {
	children: React.ReactNode;
	className?: string;
	as?: "div" | "li";
}) {
	const Component = motion[as];
	return (
		<Component className={className} variants={itemVariants}>
			{children}
		</Component>
	);
}
